/**
 * PublishCreditsCard — "Publishing Rights" dashboard widget.
 *
 * Renders the publish entitlement read model: remaining credits, an Active /
 * No-Credits status badge, and the entitlement source. It NEVER reads
 * `publishCredits` from the session — it consumes `GET /v1/me/entitlements` via
 * `usePublishEntitlement`, so a backend provider change needs no UI change.
 */
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { Icons } from '@/src/modules/components/Icons';
import { usePublishEntitlement } from '../usePublishEntitlement';
import type { EntitlementSource } from '../types';
import { EntitlementStatusBadge } from './EntitlementStatusBadge';

// Human label for the source. Beta-era sources read as "Beta Access"; future
// providers surface automatically without a redesign.
const SOURCE_LABEL: Record<EntitlementSource, string> = {
  BETA_CREDIT: 'Beta Access',
  ADMIN_GRANT: 'Beta Access',
  ENTERPRISE: 'Enterprise',
  PURCHASE: 'Purchased',
};

const CARD_CLASS =
  'bg-surface rounded-[2rem] border border-outline-variant/30 shadow-sm p-6 mb-8';

export function PublishCreditsCard() {
  const navigate = useNavigate();
  const canBuyCreditPacks = useAuthStore((s) => s.permissions).includes('PURCHASE_CREDIT_PACK');
  const { data, loading, error, reload } = usePublishEntitlement();

  return (
    <section className={CARD_CLASS} aria-label="Publishing rights">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <Icons.Upload size={20} />
        </div>
        <h2 className="text-base font-bold text-on-surface">Publishing Rights</h2>
      </div>

      {loading && (
        <div className="flex items-center gap-3" aria-busy="true">
          <div className="h-8 w-32 rounded-lg bg-surface-low animate-pulse" />
          <div className="h-6 w-20 rounded-full bg-surface-low animate-pulse" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-status-failed font-medium">{error}</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-3xl font-extrabold text-on-surface leading-none">
              {data.remainingCredits}
            </p>
            <p className="text-sm text-on-surface-variant mt-1">
              {data.canPublish
                ? `${data.remainingCredits} publish ${
                    data.remainingCredits === 1 ? 'credit' : 'credits'
                  } remaining`
                : 'No publishing credits available'}
            </p>
            {!data.canPublish && (
              <p className="text-xs text-on-surface-variant mt-1">
                Buy a publish credit for a design when you publish an invitation.
              </p>
            )}
            {/*
              Partner-only: don't offer an individual a purchase the backend will
              refuse, and don't link them to a tab they cannot open.
            */}
            {canBuyCreditPacks && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/partner?tab=credits')}
                className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 mt-2"
              >
                <span>Buy Partner Credits</span>
                <Icons.ArrowRight size={12} />
              </button>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <EntitlementStatusBadge canPublish={data.canPublish} />
            <span className="text-xs text-on-surface-variant">
              Source: {SOURCE_LABEL[data.entitlementSource] ?? 'Beta Access'}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
